import type { 
  Product, ProductCategory, Inquiry, News, Application, 
  ProductParam, Job, Honor, PageContent, SystemConfig, 
  Attachment, OperationLog, JobApplication 
} from '@/types';
import { serverStorage as idbStorage } from './serverStorage';
import { storage as legacyStorage } from './indexedDB';
import { imageIndexService } from './imageIndex';

// 存储键名
const KEYS = {
  CATEGORIES: 'hypwr_categories',
  PRODUCTS: 'hypwr_products',
  INQUIRIES: 'hypwr_inquiries',
  NEWS: 'hypwr_news',
  APPLICATIONS: 'hypwr_applications',
  PARAMS: 'hypwr_params',
  JOBS: 'hypwr_jobs',
  HONORS: 'hypwr_honors',
  PAGE_CONTENT: 'hypwr_page_content',
  SYSTEM_CONFIG: 'hypwr_system_config',
  ATTACHMENTS: 'hypwr_attachments',
  OPERATION_LOGS: 'hypwr_operation_logs',
  JOB_APPLICATIONS: 'hypwr_job_applications',
};

// 存储表名映射
const STORES = {
  CATEGORIES: 'categories',
  PRODUCTS: 'products',
  INQUIRIES: 'inquiries',
  NEWS: 'news',
  APPLICATIONS: 'applications',
  PARAMS: 'params',
  JOBS: 'jobs',
  HONORS: 'honors',
  PAGE_CONTENT: 'pageContent',
  SYSTEM_CONFIG: 'systemConfig',
  ATTACHMENTS: 'attachments',
  OPERATION_LOGS: 'operationLogs',
  JOB_APPLICATIONS: 'jobApplications',
};

// 默认系统配置
const defaultSystemConfig: SystemConfig = {
  siteName: '瀚昕微电子',
  siteKeywords: '电源管理芯片,LDO,DCDC,充电IC,协议IC',
  siteDescription: '瀚昕微电子是一家专注于高性能、高品质数模混合芯片研发的芯片设计公司',
  icpNumber: '',
  smtpHost: 'smtp.example.com',
  smtpPort: '587',
  smtpUser: '',
  smtpPassword: '',
  smtpSecure: true,
  notifyEmail: 'sales@hypwr.com',
};

// 服务器上已上传的图片路径（用于自动恢复）
const SERVER_IMAGES = {
  logo: '/uploads/1776129660795_7974.png', // 瀚昕微电子LOGO
  heroBg: '/uploads/1776129568152_8593.png', // Hero科技背景大图
  aboutImage: '/uploads/1776129622981_5306.jpg', // 办公室大楼
  qrCode: '/uploads/19d8f3f5-5812-845b-8000-0000fe735a87_瀚昕微无锡二维码-ONEPAGE.png', // 微信公众号二维码
  applicationImages: {
    consumer: '/uploads/1776129500611_6306.png', // 智能家电场景
    security: '/uploads/1776129478105_8543.png', // 安防监控场景
  }
};

// 默认页面内容
const defaultPageContent: PageContent = {
  logo: SERVER_IMAGES.logo,
  hero: {
    title: '专注于高性能、高品质\n数模混合芯片研发的\n芯片设计公司',
    subtitle: 'Focus on high performance, high quality analog-digital ASIC',
    titleEn: '',
    backgroundImage: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=1920&q=80',
    buttonText: '浏览产品',
    buttonLink: '/products',
    titleFontSize: '48',
    titleLineHeight: '1.4',
    subtitleFontSize: '20',
    titleColor: '#ffffff',
    subtitleColor: '#ffffff',
  },
  about: {
    title: '关于瀚昕微',
    content: [
      '瀚昕微电子缘起于2017年，2019年在无锡注册成立，是一家专注于高性能、高品质数模混合芯片研发的芯片设计公司。',
      '公司致力于研发电能-储能转换应用中的高性能电源管理芯片产品。',
      '公司核心团队均来自国内外知名学府，在IR、PI、Belling、Cypress等国内外知名半导体企业任职多年，深耕电源IC设计领域，具有丰富的行业经验。',
      '公司产品广泛应用于手机、PC、TWS耳机、智能穿戴、智能家电等消费类终端，并向工业、安防和汽车领域延伸。',
      '公司成立以来，已先后获得了TCL资本、小米产投及上游代工厂的战略投资。',
    ],
    image: "",
    video: "",
    backgroundImage: '',
    fontSize: '16',
    lineHeight: '1.8',
  },
  philosophy: {
    title: '经营理念',
    backgroundImage: '',
    items: [
      {
        title: '专注',
        description: '专注于电源管理芯片领域，持续深耕',
        image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=400&q=80',
        fontSize: '16',
        textColor: '#ffffff',
      },
      {
        title: '创新',
        description: '技术创新驱动，不断突破自我',
        image: 'https://images.unsplash.com/photo-1519389950476-29a8e752cc80?w=400&q=80',
        fontSize: '16',
        textColor: '#ffffff',
      },
      {
        title: '品质',
        description: '追求卓越品质，打造可靠产品',
        image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&q=80',
        fontSize: '16',
        textColor: '#ffffff',
      },
      {
        title: '共赢',
        description: '与客户、合作伙伴共同成长',
        image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&q=80',
        fontSize: '16',
        textColor: '#ffffff',
      },
    ],
  },
  productsIntro: {
    title: '产品中心',
    description: '瀚昕微可提供包括LDO线性稳压器、过压过流保护IC、线性充电IC、DCDC功率转换器、协议IC、LED驱动等系列产品。',
    backgroundImage: '',
  },
  news: {
    backgroundImage: '',
  },
  partners: {
    backgroundImage: '',
    partnerLogos: [],
  },
  footer: {
    backgroundImage: '',
    qrCode: SERVER_IMAGES.qrCode,
    companyLogo: SERVER_IMAGES.logo,
  },
  qualitySystem: {
    isoImage: '',
  },
};

// 同步从 localStorage 读取（用于初始化回退）
const getLocalData = <T>(key: string): T | null => {
  try {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  } catch {
    return null;
  }
};

// 初始化默认数据（异步）
export async function initializeDefaultData() {
  try {
    // === 数据迁移：如果服务器为空且本地 IndexedDB 有旧数据，先迁移到服务器 ===
    const serverCategories = await idbStorage.getList<ProductCategory>(STORES.CATEGORIES);
    if (!serverCategories || serverCategories.length === 0) {
      console.log('服务器数据为空，检查本地 IndexedDB 是否有旧数据...');
      
      const localCategories = await legacyStorage.getList<ProductCategory>(STORES.CATEGORIES);
      if (localCategories && localCategories.length > 0) {
        console.log('发现本地旧数据，开始迁移到服务器...');
        
        // 迁移所有本地数据到服务器
        const allLocalData = await Promise.all([
          legacyStorage.getList<ProductCategory>(STORES.CATEGORIES),
          legacyStorage.getItem<Record<string, ProductParam[]>>(KEYS.PARAMS, STORES.PARAMS),
          legacyStorage.getList<Product>(STORES.PRODUCTS),
          legacyStorage.getList<Application>(STORES.APPLICATIONS),
          legacyStorage.getList<News>(STORES.NEWS),
          legacyStorage.getList<Inquiry>(STORES.INQUIRIES),
          legacyStorage.getList<Job>(STORES.JOBS),
          legacyStorage.getList<Honor>(STORES.HONORS),
          legacyStorage.getItem<PageContent>(KEYS.PAGE_CONTENT, STORES.PAGE_CONTENT),
          legacyStorage.getItem<SystemConfig>(KEYS.SYSTEM_CONFIG, STORES.SYSTEM_CONFIG),
          legacyStorage.getList<Attachment>(STORES.ATTACHMENTS),
          legacyStorage.getList<OperationLog>(STORES.OPERATION_LOGS),
          legacyStorage.getList<JobApplication>(STORES.JOB_APPLICATIONS),
        ]);
        
        await Promise.all([
          idbStorage.saveList(KEYS.CATEGORIES, STORES.CATEGORIES, allLocalData[0] || []),
          idbStorage.setItem(KEYS.PARAMS, STORES.PARAMS, allLocalData[1] || {}),
          idbStorage.saveList(KEYS.PRODUCTS, STORES.PRODUCTS, allLocalData[2] || []),
          idbStorage.saveList(KEYS.APPLICATIONS, STORES.APPLICATIONS, allLocalData[3] || []),
          idbStorage.saveList(KEYS.NEWS, STORES.NEWS, allLocalData[4] || []),
          idbStorage.saveList(KEYS.INQUIRIES, STORES.INQUIRIES, allLocalData[5] || []),
          idbStorage.saveList(KEYS.JOBS, STORES.JOBS, allLocalData[6] || []),
          idbStorage.saveList(KEYS.HONORS, STORES.HONORS, allLocalData[7] || []),
          idbStorage.setItem(KEYS.PAGE_CONTENT, STORES.PAGE_CONTENT, allLocalData[8] || defaultPageContent),
          idbStorage.setItem(KEYS.SYSTEM_CONFIG, STORES.SYSTEM_CONFIG, allLocalData[9] || defaultSystemConfig),
          idbStorage.saveList(KEYS.ATTACHMENTS, STORES.ATTACHMENTS, allLocalData[10] || []),
          idbStorage.saveList(KEYS.OPERATION_LOGS, STORES.OPERATION_LOGS, allLocalData[11] || []),
          idbStorage.saveList(KEYS.JOB_APPLICATIONS, STORES.JOB_APPLICATIONS, allLocalData[12] || []),
        ]);
        
        console.log('本地数据迁移完成！');
        
        // 迁移完成后，继续执行后续同步逻辑
        // 不再执行默认数据初始化，因为已经有数据了
        
        // 同步服务器图片索引
        try {
          console.log('开始同步服务器图片索引...');
          const discovered = await imageIndexService.autoDiscoverImages();
          console.log('发现服务器图片:', discovered);
          
          const pageContent = await idbStorage.getItem<PageContent>(KEYS.PAGE_CONTENT, STORES.PAGE_CONTENT);
          if (pageContent) {
            let updated = false;
            
            if (!pageContent.logo && discovered.logo) {
              pageContent.logo = discovered.logo;
              updated = true;
            }
            
            if (discovered.hero && !pageContent.hero?.backgroundImage?.startsWith('/uploads/')) {
              pageContent.hero = { ...pageContent.hero, backgroundImage: discovered.hero };
              updated = true;
            }
            
            if (discovered.about && !pageContent.about?.image?.startsWith('/uploads/')) {
              pageContent.about = { ...pageContent.about, image: discovered.about };
              updated = true;
            }
            
            if (discovered.qrCode && !pageContent.footer?.qrCode?.startsWith('/uploads/')) {
              pageContent.footer = { 
                ...pageContent.footer, 
                qrCode: discovered.qrCode,
                companyLogo: pageContent.footer?.companyLogo || discovered.logo || pageContent.logo
              };
              updated = true;
            }
            
            if (updated) {
              await idbStorage.setItem(KEYS.PAGE_CONTENT, STORES.PAGE_CONTENT, pageContent);
              console.log('页面内容图片已更新');
            }
          }
          
          if (discovered.news.length > 0) {
            const news = await idbStorage.getList<News>(STORES.NEWS);
            let newsUpdated = false;
            news.forEach((item, index) => {
              if (!item.image && discovered.news[index]) {
                item.image = discovered.news[index];
                newsUpdated = true;
              }
            });
            if (newsUpdated) {
              await idbStorage.saveList(KEYS.NEWS, STORES.NEWS, news);
              console.log('新闻图片已更新');
            }
          }
          
          if (discovered.applications.length > 0) {
            const apps = await idbStorage.getList<Application>(STORES.APPLICATIONS);
            let appsUpdated = false;
            apps.forEach((app, index) => {
              if (!app.image && discovered.applications[index]) {
                app.image = discovered.applications[index];
                appsUpdated = true;
              }
            });
            if (appsUpdated) {
              await idbStorage.saveList(KEYS.APPLICATIONS, STORES.APPLICATIONS, apps);
              console.log('应用领域图片已更新');
            }
          }
        } catch (e) {
          console.warn('服务器图片同步失败:', e);
        }
        
        return; // 迁移完成，跳过默认数据初始化
      }
    }
    // 默认产品分类（带排序）
    const defaultCategories: ProductCategory[] = [
      { id: '1', name: 'LDO线性稳压器', nameEn: 'LDO', description: '低 dropout 线性稳压器', sortOrder: 1 },
      { id: '2', name: '过压过流保护IC', nameEn: 'OVP and OCP IC', description: '过压过流保护芯片', sortOrder: 2 },
      { id: '3', name: '线性充电IC', nameEn: 'Linear charging IC', description: '锂电池线性充电芯片', sortOrder: 3 },
      { id: '4', name: 'DCDC功率转换器', nameEn: 'DCDC', description: 'DC-DC开关电源转换器', sortOrder: 4 },
      { id: '5', name: '协议IC', nameEn: 'Protocol Interface IC', description: 'USB PD快充协议芯片', sortOrder: 5 },
      { id: '6', name: '其他产品', nameEn: 'Others', description: '其他电源管理芯片', sortOrder: 99 },
    ];

    // 默认产品参数定义
    const defaultParams: Record<string, ProductParam[]> = {
      '1': [
        { key: 'iout', label: 'I_OUT', unit: 'mA@Max', type: 'number' },
        { key: 'vin', label: 'V_IN', unit: 'V', type: 'text' },
        { key: 'vout', label: 'V_OUT', unit: 'V', type: 'text' },
        { key: 'iq', label: 'I_Q', unit: 'uA@Typ', type: 'number' },
        { key: 'dropout', label: 'Dropout Voltage', unit: 'mV@Typ', type: 'text' },
        { key: 'psrr', label: 'PSRR', unit: 'dB', type: 'text' },
        { key: 'discharge', label: 'Output Active Discharge', type: 'boolean' },
        { key: 'adj', label: 'Adj. Version', type: 'boolean' },
        { key: 'enable', label: 'Enable Pin', type: 'boolean' },
        { key: 'package', label: 'Package', type: 'text' },
        { key: 'application', label: 'Application', type: 'text' },
      ],
      '2': [
        { key: 'vin', label: 'V_IN', unit: 'V', type: 'text' },
        { key: 'vovp', label: 'V_OVP', unit: 'V', type: 'text' },
        { key: 'iocp', label: 'I_OCP', unit: 'A', type: 'text' },
        { key: 'ron', label: 'R_ON', unit: 'mΩ', type: 'number' },
        { key: 'package', label: 'Package', type: 'text' },
      ],
      '3': [
        { key: 'ichg', label: 'I_CHG', unit: 'mA', type: 'text' },
        { key: 'vin', label: 'V_IN', unit: 'V', type: 'text' },
        { key: 'vbat', label: 'V_BAT', unit: 'V', type: 'text' },
        { key: 'package', label: 'Package', type: 'text' },
      ],
      '4': [
        { key: 'vin', label: 'V_IN', unit: 'V', type: 'text' },
        { key: 'vout', label: 'V_OUT', unit: 'V', type: 'text' },
        { key: 'iout', label: 'I_OUT', unit: 'mA', type: 'number' },
        { key: 'freq', label: 'Frequency', unit: 'kHz', type: 'number' },
        { key: 'efficiency', label: 'Efficiency', unit: '%', type: 'number' },
        { key: 'package', label: 'Package', type: 'text' },
      ],
      '5': [
        { key: 'protocol', label: 'Protocol', type: 'text' },
        { key: 'vin', label: 'V_IN', unit: 'V', type: 'text' },
        { key: 'vout', label: 'V_OUT', unit: 'V', type: 'text' },
        { key: 'package', label: 'Package', type: 'text' },
      ],
    };

    // 默认产品数据
    const defaultProducts: Product[] = [
      {
        id: '102',
        model: 'HP1117',
        categoryId: '1',
        parameters: {
          iout: 1000,
          vin: '18',
          vout: '1.2 ~ 5',
          iq: 2000,
          dropout: '1300 @1A',
          psrr: '70@120Hz',
          discharge: false,
          adj: true,
          enable: false,
          package: 'SOT223, TO252, SOT89-3L',
          application: 'TV/Home Appliance/STB',
        },
        createdAt: '2024-01-01',
        updatedAt: '2024-01-01',
      },
      {
        id: '103',
        model: 'HP6011',
        categoryId: '1',
        parameters: {
          iout: 400,
          vin: '1.7~8',
          vout: '1.0 ~ 3.6',
          iq: 70,
          dropout: '130 @300mA',
          psrr: '70@1kHz',
          discharge: false,
          adj: false,
          enable: true,
          package: 'SOT23-5L, DFN1*1-4L',
          application: 'CIS/Router/Miner',
        },
        createdAt: '2024-01-01',
        updatedAt: '2024-01-01',
      },
    ];

    // 默认应用领域
    const defaultApplications: Application[] = [
      { id: '1', title: '消费电子', titleEn: 'Consumer electronics', description: '手机、PC、TWS耳机、智能穿戴、智能家电等消费类终端' },
      { id: '2', title: '工业能源', titleEn: 'Industrial & Automotive', description: '工业控制、汽车电子、能源管理系统' },
      { id: '3', title: '电动工具', titleEn: 'Electric Tools', description: '电动工具、园林工具、便携式设备' },
      { id: '4', title: '安防监控', titleEn: 'Security monitoring', description: '安防摄像头、监控系统、门禁设备' },
      { id: '5', title: '其他', titleEn: 'Others', description: '其他应用领域' },
    ];

    // 默认新闻
    const defaultNews: News[] = [
      {
        id: '13',
        title: '瀚昕微eEmarker芯片系列产品陆续通过USB-IF PD3+ 认证',
        summary: '瀚昕微eEmarker芯片系列产品成功通过USB-IF PD3+认证，标志着产品性能达到国际标准。',
        date: '2024-03-15',
        category: 'company',
      },
    ];

    // 默认招聘职位
    const defaultJobs: Job[] = [
      {
        id: '1',
        title: '模拟IC设计工程师',
        department: '研发部',
        location: '无锡',
        type: '全职',
        experience: '3-5年',
        description: '负责模拟IC的设计和验证工作',
        requirements: [
          '电子工程、微电子等相关专业硕士及以上学历',
          '3年以上模拟IC设计经验',
        ],
        sortOrder: 1,
        isActive: true,
      },
    ];

    // 默认荣誉资质
    const defaultHonors: Honor[] = [
      { id: '1', title: '国家高新技术企业', image: '', year: '2021', sortOrder: 1 },
      { id: '2', title: '江苏省专精特新中小企业', image: '', year: '2022', sortOrder: 2 },
    ];

    // 检查并初始化分类
    const existingCategories = await idbStorage.getList<ProductCategory>(STORES.CATEGORIES);
    if (!existingCategories || existingCategories.length === 0) {
      const localCategories = getLocalData<ProductCategory[]>(KEYS.CATEGORIES);
      await idbStorage.saveList(KEYS.CATEGORIES, STORES.CATEGORIES, localCategories || defaultCategories);
    }

    // 初始化产品参数
    const existingParams = await idbStorage.getItem<Record<string, ProductParam[]>>(KEYS.PARAMS, STORES.PARAMS);
    if (!existingParams) {
      const localParams = getLocalData<Record<string, ProductParam[]>>(KEYS.PARAMS);
      await idbStorage.setItem(KEYS.PARAMS, STORES.PARAMS, localParams || defaultParams);
    }

    // 初始化产品
    const existingProducts = await idbStorage.getList<Product>(STORES.PRODUCTS);
    if (!existingProducts || existingProducts.length === 0) {
      const localProducts = getLocalData<Product[]>(KEYS.PRODUCTS);
      await idbStorage.saveList(KEYS.PRODUCTS, STORES.PRODUCTS, localProducts || defaultProducts);
    }

    // 初始化应用领域
    const existingApps = await idbStorage.getList<Application>(STORES.APPLICATIONS);
    if (!existingApps || existingApps.length === 0) {
      const localApps = getLocalData<Application[]>(KEYS.APPLICATIONS);
      const mergedApps = (localApps || defaultApplications).map((app: any) => ({
        ...defaultApplications.find((d: any) => d.id === app.id),
        ...app,
        image: app.image ?? '',
      }));
      await idbStorage.saveList(KEYS.APPLICATIONS, STORES.APPLICATIONS, mergedApps);
    }

    // 初始化新闻
    const existingNews = await idbStorage.getList<News>(STORES.NEWS);
    if (!existingNews || existingNews.length === 0) {
      const localNews = getLocalData<News[]>(KEYS.NEWS);
      const mergedNews = (localNews || defaultNews).map((news: any) => ({
        ...news,
        image: news.image ?? '',
      }));
      await idbStorage.saveList(KEYS.NEWS, STORES.NEWS, mergedNews);
    }

    // 初始化询单
    const existingInquiries = await idbStorage.getList<Inquiry>(STORES.INQUIRIES);
    if (!existingInquiries || existingInquiries.length === 0) {
      const localInquiries = getLocalData<Inquiry[]>(KEYS.INQUIRIES);
      await idbStorage.saveList(KEYS.INQUIRIES, STORES.INQUIRIES, localInquiries || []);
    }

    // 初始化职位
    const existingJobs = await idbStorage.getList<Job>(STORES.JOBS);
    if (!existingJobs || existingJobs.length === 0) {
      const localJobs = getLocalData<Job[]>(KEYS.JOBS);
      await idbStorage.saveList(KEYS.JOBS, STORES.JOBS, localJobs || defaultJobs);
    }

    // 初始化荣誉资质
    const existingHonors = await idbStorage.getList<Honor>(STORES.HONORS);
    if (!existingHonors || existingHonors.length === 0) {
      const localHonors = getLocalData<Honor[]>(KEYS.HONORS);
      const mergedHonors = (localHonors || defaultHonors).map((honor: any) => ({
        ...honor,
        image: honor.image ?? '',
      }));
      await idbStorage.saveList(KEYS.HONORS, STORES.HONORS, mergedHonors);
    }

    // 初始化页面内容
    const existingPageContent = await idbStorage.getItem<PageContent>(KEYS.PAGE_CONTENT, STORES.PAGE_CONTENT);
    if (!existingPageContent) {
      const localPageContent = getLocalData<PageContent>(KEYS.PAGE_CONTENT);
      const existingData = localPageContent || defaultPageContent;
      const mergedData: PageContent = {
        ...defaultPageContent,
        ...existingData,
        hero: { ...defaultPageContent.hero, ...(existingData.hero || {}) },
        about: { ...defaultPageContent.about, ...(existingData.about || {}) },
        philosophy: {
          ...defaultPageContent.philosophy,
          ...(existingData.philosophy || {}),
          items: existingData.philosophy?.items || defaultPageContent.philosophy.items,
        },
        productsIntro: { ...defaultPageContent.productsIntro, ...(existingData.productsIntro || {}) },
        news: { ...defaultPageContent.news, ...(existingData.news || {}) },
        partners: {
          ...defaultPageContent.partners,
          ...(existingData.partners || {}),
          partnerLogos: existingData.partners?.partnerLogos ?? defaultPageContent.partners.partnerLogos,
        },
        footer: {
          ...defaultPageContent.footer,
          ...(existingData.footer || {}),
          qrCode: existingData.footer?.qrCode ?? defaultPageContent.footer.qrCode,
          companyLogo: existingData.footer?.companyLogo ?? defaultPageContent.footer.companyLogo,
        },
        qualitySystem: {
          ...defaultPageContent.qualitySystem!,
          ...(existingData.qualitySystem || {}),
          isoImage: existingData.qualitySystem?.isoImage ?? defaultPageContent.qualitySystem!.isoImage,
        },
      };
      await idbStorage.setItem(KEYS.PAGE_CONTENT, STORES.PAGE_CONTENT, mergedData);
    }

    // 初始化系统配置
    const existingConfig = await idbStorage.getItem<SystemConfig>(KEYS.SYSTEM_CONFIG, STORES.SYSTEM_CONFIG);
    if (!existingConfig) {
      const localConfig = getLocalData<SystemConfig>(KEYS.SYSTEM_CONFIG);
      await idbStorage.setItem(KEYS.SYSTEM_CONFIG, STORES.SYSTEM_CONFIG, localConfig || defaultSystemConfig);
    }

    // 初始化附件
    const existingAttachments = await idbStorage.getList<Attachment>(STORES.ATTACHMENTS);
    if (!existingAttachments || existingAttachments.length === 0) {
      const localAttachments = getLocalData<Attachment[]>(KEYS.ATTACHMENTS);
      await idbStorage.saveList(KEYS.ATTACHMENTS, STORES.ATTACHMENTS, localAttachments || []);
    }

    // 初始化操作日志
    const existingLogs = await idbStorage.getList<OperationLog>(STORES.OPERATION_LOGS);
    if (!existingLogs || existingLogs.length === 0) {
      const localLogs = getLocalData<OperationLog[]>(KEYS.OPERATION_LOGS);
      await idbStorage.saveList(KEYS.OPERATION_LOGS, STORES.OPERATION_LOGS, localLogs || []);
    }

    // 初始化简历投递
    const existingJobApps = await idbStorage.getList<JobApplication>(STORES.JOB_APPLICATIONS);
    if (!existingJobApps || existingJobApps.length === 0) {
      const localJobApps = getLocalData<JobApplication[]>(KEYS.JOB_APPLICATIONS);
      await idbStorage.saveList(KEYS.JOB_APPLICATIONS, STORES.JOB_APPLICATIONS, localJobApps || []);
    }

    console.log('数据初始化完成');
    
    // 同步服务器图片索引
    try {
      console.log('开始同步服务器图片索引...');
      const discovered = await imageIndexService.autoDiscoverImages();
      console.log('发现服务器图片:', discovered);
      
      // 更新页面内容中的图片路径（如果本地为空）
      const pageContent = await idbStorage.getItem<PageContent>(KEYS.PAGE_CONTENT, STORES.PAGE_CONTENT);
      if (pageContent) {
        let updated = false;
        
        // 更新 LOGO
        if (!pageContent.logo && discovered.logo) {
          pageContent.logo = discovered.logo;
          updated = true;
        }
        
        // 更新 Hero 背景图
        if (discovered.hero && !pageContent.hero?.backgroundImage?.startsWith('/uploads/')) {
          pageContent.hero = { ...pageContent.hero, backgroundImage: discovered.hero };
          updated = true;
        }
        
        // 更新关于我们图片
        if (discovered.about && !pageContent.about?.image?.startsWith('/uploads/')) {
          pageContent.about = { ...pageContent.about, image: discovered.about };
          updated = true;
        }
        
        // 更新页脚二维码
        if (discovered.qrCode && !pageContent.footer?.qrCode?.startsWith('/uploads/')) {
          pageContent.footer = { 
            ...pageContent.footer, 
            qrCode: discovered.qrCode,
            companyLogo: pageContent.footer?.companyLogo || discovered.logo || pageContent.logo
          };
          updated = true;
        }
        
        if (updated) {
          await idbStorage.setItem(KEYS.PAGE_CONTENT, STORES.PAGE_CONTENT, pageContent);
          console.log('页面内容图片已更新');
        }
      }
      
      // 更新新闻图片
      if (discovered.news.length > 0) {
        const news = await idbStorage.getList<News>(STORES.NEWS);
        let newsUpdated = false;
        news.forEach((item, index) => {
          if (!item.image && discovered.news[index]) {
            item.image = discovered.news[index];
            newsUpdated = true;
          }
        });
        if (newsUpdated) {
          await idbStorage.saveList(KEYS.NEWS, STORES.NEWS, news);
          console.log('新闻图片已更新');
        }
      }
      
      // 更新应用领域图片
      if (discovered.applications.length > 0) {
        const apps = await idbStorage.getList<Application>(STORES.APPLICATIONS);
        let appsUpdated = false;
        apps.forEach((app, index) => {
          if (!app.image && discovered.applications[index]) {
            app.image = discovered.applications[index];
            appsUpdated = true;
          }
        });
        if (appsUpdated) {
          await idbStorage.saveList(KEYS.APPLICATIONS, STORES.APPLICATIONS, apps);
          console.log('应用领域图片已更新');
        }
      }
      
    } catch (e) {
      console.warn('服务器图片同步失败:', e);
    }
  } catch (error) {
    console.warn('数据初始化失败:', error);
  }
}

// 产品分类CRUD
export const categoryService = {
  getAll: async (): Promise<ProductCategory[]> => {
    const categories = await idbStorage.getList<ProductCategory>(STORES.CATEGORIES);
    return categories.sort((a: ProductCategory, b: ProductCategory) => a.sortOrder - b.sortOrder);
  },
  getById: async (id: string): Promise<ProductCategory | undefined> => {
    const categories = await categoryService.getAll();
    return categories.find(c => c.id === id);
  },
  getTopLevel: async (): Promise<ProductCategory[]> => {
    const categories = await categoryService.getAll();
    return categories.filter(c => !c.parentId);
  },
  getChildren: async (parentId: string): Promise<ProductCategory[]> => {
    const categories = await categoryService.getAll();
    return categories.filter(c => c.parentId === parentId);
  },
  save: async (categories: ProductCategory[]) => {
    await idbStorage.saveList(KEYS.CATEGORIES, STORES.CATEGORIES, categories);
  },
  add: async (category: ProductCategory) => {
    const categories = await categoryService.getAll();
    categories.push(category);
    await categoryService.save(categories);
  },
  update: async (id: string, category: Partial<ProductCategory>) => {
    const categories = await categoryService.getAll();
    const index = categories.findIndex(c => c.id === id);
    if (index !== -1) {
      categories[index] = { ...categories[index], ...category };
      await categoryService.save(categories);
    }
  },
  delete: async (id: string) => {
    const categories = await categoryService.getAll();
    await categoryService.save(categories.filter(c => c.id !== id && c.parentId !== id));
  },
  updateSortOrder: async (id: string, newOrder: number) => {
    const categories = await categoryService.getAll();
    const index = categories.findIndex(c => c.id === id);
    if (index !== -1) {
      categories[index].sortOrder = newOrder;
      await categoryService.save(categories);
    }
  },
};

// 产品参数CRUD
export const paramService = {
  getByCategory: async (categoryId: string): Promise<ProductParam[]> => {
    const raw = await idbStorage.getItem<any>(KEYS.PARAMS, STORES.PARAMS) || {};
    // 防御：服务器数据可能是数组或字典
    let params: Record<string, ProductParam[]> = {};
    if (Array.isArray(raw)) {
      //  polluted 数组：找第一个有效字典
      for (const item of raw) {
        if (item && typeof item === 'object' && !Array.isArray(item) && item[categoryId]) {
          params = item;
          break;
        }
      }
    } else if (raw && typeof raw === 'object') {
      params = raw;
    }
    return params[categoryId] || [];
  },
  save: async (categoryId: string, params: ProductParam[]) => {
    const allParams = await idbStorage.getItem<Record<string, ProductParam[]>>(KEYS.PARAMS, STORES.PARAMS) || {};
    allParams[categoryId] = params;
    await idbStorage.setItem(KEYS.PARAMS, STORES.PARAMS, allParams);
  },
};

// 产品CRUD
export const productService = {
  getAll: async (): Promise<Product[]> => {
    return await idbStorage.getList<Product>(STORES.PRODUCTS);
  },
  getById: async (id: string): Promise<Product | undefined> => {
    const products = await productService.getAll();
    return products.find(p => p.id === id);
  },
  getByCategory: async (categoryId: string): Promise<Product[]> => {
    const products = await productService.getAll();
    return products.filter(p => p.categoryId === categoryId);
  },
  save: async (products: Product[]) => {
    await idbStorage.saveList(KEYS.PRODUCTS, STORES.PRODUCTS, products);
  },
  add: async (product: Product) => {
    const products = await productService.getAll();
    products.push(product);
    await productService.save(products);
  },
  update: async (id: string, product: Partial<Product>) => {
    const products = await productService.getAll();
    const index = products.findIndex(p => p.id === id);
    if (index !== -1) {
      products[index] = { ...products[index], ...product, updatedAt: new Date().toISOString() };
      await productService.save(products);
    }
  },
  delete: async (id: string) => {
    const products = await productService.getAll();
    await productService.save(products.filter(p => p.id !== id));
  },
};

// 询单CRUD
export const inquiryService = {
  getAll: async (): Promise<Inquiry[]> => {
    return await idbStorage.getList<Inquiry>(STORES.INQUIRIES);
  },
  getById: async (id: string): Promise<Inquiry | undefined> => {
    const inquiries = await inquiryService.getAll();
    return inquiries.find(i => i.id === id);
  },
  save: async (inquiries: Inquiry[]) => {
    await idbStorage.saveList(KEYS.INQUIRIES, STORES.INQUIRIES, inquiries);
  },
  add: async (inquiry: Inquiry) => {
    const inquiries = await inquiryService.getAll();
    inquiries.push(inquiry);
    await inquiryService.save(inquiries);
  },
  update: async (id: string, inquiry: Partial<Inquiry>) => {
    const inquiries = await inquiryService.getAll();
    const index = inquiries.findIndex(i => i.id === id);
    if (index !== -1) {
      inquiries[index] = { ...inquiries[index], ...inquiry };
      await inquiryService.save(inquiries);
    }
  },
  delete: async (id: string) => {
    const inquiries = await inquiryService.getAll();
    await inquiryService.save(inquiries.filter(i => i.id !== id));
  },
};

// 新闻CRUD
export const newsService = {
  getAll: async (): Promise<News[]> => {
    return await idbStorage.getList<News>(STORES.NEWS);
  },
  getById: async (id: string): Promise<News | undefined> => {
    const news = await newsService.getAll();
    return news.find(n => n.id === id);
  },
  save: async (news: News[]) => {
    await idbStorage.saveList(KEYS.NEWS, STORES.NEWS, news);
  },
  add: async (news: News) => {
    const allNews = await newsService.getAll();
    allNews.push(news);
    await newsService.save(allNews);
  },
  update: async (id: string, news: Partial<News>) => {
    const allNews = await newsService.getAll();
    const index = allNews.findIndex(n => n.id === id);
    if (index !== -1) {
      allNews[index] = { ...allNews[index], ...news };
      await newsService.save(allNews);
    }
  },
  delete: async (id: string) => {
    const allNews = await newsService.getAll();
    await newsService.save(allNews.filter(n => n.id !== id));
  },
};

// 应用领域CRUD
export const applicationService = {
  getAll: async (): Promise<Application[]> => {
    return await idbStorage.getList<Application>(STORES.APPLICATIONS);
  },
  getById: async (id: string): Promise<Application | undefined> => {
    const applications = await applicationService.getAll();
    return applications.find(a => a.id === id);
  },
  save: async (applications: Application[]) => {
    await idbStorage.saveList(KEYS.APPLICATIONS, STORES.APPLICATIONS, applications);
  },
  add: async (application: Application) => {
    const applications = await applicationService.getAll();
    applications.push(application);
    await applicationService.save(applications);
  },
  update: async (id: string, application: Partial<Application>) => {
    const applications = await applicationService.getAll();
    const index = applications.findIndex(a => a.id === id);
    if (index !== -1) {
      applications[index] = { ...applications[index], ...application };
      await applicationService.save(applications);
    }
  },
  delete: async (id: string) => {
    const applications = await applicationService.getAll();
    await applicationService.save(applications.filter(a => a.id !== id));
  },
};

// 招聘职位CRUD
export const jobService = {
  getAll: async (): Promise<Job[]> => {
    const jobs = await idbStorage.getList<Job>(STORES.JOBS);
    return jobs.sort((a: Job, b: Job) => a.sortOrder - b.sortOrder);
  },
  getActive: async (): Promise<Job[]> => {
    const jobs = await jobService.getAll();
    return jobs.filter(j => j.isActive);
  },
  getById: async (id: string): Promise<Job | undefined> => {
    const jobs = await jobService.getAll();
    return jobs.find(j => j.id === id);
  },
  save: async (jobs: Job[]) => {
    await idbStorage.saveList(KEYS.JOBS, STORES.JOBS, jobs);
  },
  add: async (job: Job) => {
    const jobs = await jobService.getAll();
    jobs.push(job);
    await jobService.save(jobs);
  },
  update: async (id: string, job: Partial<Job>) => {
    const jobs = await jobService.getAll();
    const index = jobs.findIndex(j => j.id === id);
    if (index !== -1) {
      jobs[index] = { ...jobs[index], ...job };
      await jobService.save(jobs);
    }
  },
  delete: async (id: string) => {
    const jobs = await jobService.getAll();
    await jobService.save(jobs.filter(j => j.id !== id));
  },
};

// 荣誉资质CRUD
export const honorService = {
  getAll: async (): Promise<Honor[]> => {
    const honors = await idbStorage.getList<Honor>(STORES.HONORS);
    return honors.sort((a: Honor, b: Honor) => a.sortOrder - b.sortOrder);
  },
  getById: async (id: string): Promise<Honor | undefined> => {
    const honors = await honorService.getAll();
    return honors.find(h => h.id === id);
  },
  save: async (honors: Honor[]) => {
    await idbStorage.saveList(KEYS.HONORS, STORES.HONORS, honors);
  },
  add: async (honor: Honor) => {
    const honors = await honorService.getAll();
    honors.push(honor);
    await honorService.save(honors);
  },
  update: async (id: string, honor: Partial<Honor>) => {
    const honors = await honorService.getAll();
    const index = honors.findIndex(h => h.id === id);
    if (index !== -1) {
      honors[index] = { ...honors[index], ...honor };
      await honorService.save(honors);
    }
  },
  delete: async (id: string) => {
    const honors = await honorService.getAll();
    await honorService.save(honors.filter(h => h.id !== id));
  },
};

// 页面内容CRUD
export const pageContentService = {
  get: async (): Promise<PageContent> => {
    const stored = await idbStorage.getItem<PageContent>(KEYS.PAGE_CONTENT, STORES.PAGE_CONTENT);
    
    // 辅助函数：如果值为空，使用默认值
    const orDefault = (value: any, defaultValue: any) => {
      if (value === undefined || value === null || value === '') {
        return defaultValue;
      }
      return value;
    };
    
    if (!stored) return defaultPageContent;
    
    // 合并存储的数据和默认数据，空字符串使用默认值
    return {
      ...defaultPageContent,
      ...stored,
      logo: orDefault(stored.logo, defaultPageContent.logo),
      hero: { 
        ...defaultPageContent.hero, 
        ...(stored.hero || {}),
        backgroundImage: orDefault(stored.hero?.backgroundImage, defaultPageContent.hero.backgroundImage),
      },
      about: { 
        ...defaultPageContent.about, 
        ...(stored.about || {}),
        image: orDefault(stored.about?.image, defaultPageContent.about.image),
      },
      philosophy: {
        ...defaultPageContent.philosophy,
        ...(stored.philosophy || {}),
        items: stored.philosophy?.items || defaultPageContent.philosophy.items,
      },
      productsIntro: { 
        ...defaultPageContent.productsIntro, 
        ...(stored.productsIntro || {}) 
      },
      news: { 
        ...defaultPageContent.news, 
        ...(stored.news || {}) 
      },
      partners: {
        ...defaultPageContent.partners,
        ...(stored.partners || {}),
        partnerLogos: stored.partners?.partnerLogos ?? defaultPageContent.partners.partnerLogos,
      },
      footer: {
        ...defaultPageContent.footer,
        ...(stored.footer || {}),
        qrCode: orDefault(stored.footer?.qrCode, defaultPageContent.footer.qrCode),
        companyLogo: orDefault(stored.footer?.companyLogo, defaultPageContent.footer.companyLogo),
      },
      qualitySystem: {
        ...defaultPageContent.qualitySystem!,
        ...(stored.qualitySystem || {}),
        isoImage: orDefault(stored.qualitySystem?.isoImage, defaultPageContent.qualitySystem!.isoImage),
      },
    };
  },
  save: async (content: PageContent) => {
    await idbStorage.setItem(KEYS.PAGE_CONTENT, STORES.PAGE_CONTENT, content);
  },
  update: async (section: keyof PageContent, data: any) => {
    const content = await pageContentService.get();
    const existingData = content[section] as Record<string, any> || {};
    content[section] = { ...existingData, ...data } as any;
    await pageContentService.save(content);
  },
};

// 系统配置CRUD
export const systemConfigService = {
  get: async (): Promise<SystemConfig> => {
    return await idbStorage.getItem<SystemConfig>(KEYS.SYSTEM_CONFIG, STORES.SYSTEM_CONFIG) || defaultSystemConfig;
  },
  save: async (config: SystemConfig) => {
    await idbStorage.setItem(KEYS.SYSTEM_CONFIG, STORES.SYSTEM_CONFIG, config);
  },
  update: async (data: Partial<SystemConfig>) => {
    const config = await systemConfigService.get();
    await systemConfigService.save({ ...config, ...data });
  },
};

// 附件管理CRUD
export const attachmentService = {
  getAll: async (): Promise<Attachment[]> => {
    return await idbStorage.getList<Attachment>(STORES.ATTACHMENTS);
  },
  getById: async (id: string): Promise<Attachment | undefined> => {
    const attachments = await attachmentService.getAll();
    return attachments.find(a => a.id === id);
  },
  save: async (attachments: Attachment[]) => {
    await idbStorage.saveList(KEYS.ATTACHMENTS, STORES.ATTACHMENTS, attachments);
  },
  add: async (attachment: Attachment) => {
    const attachments = await attachmentService.getAll();
    attachments.push(attachment);
    await attachmentService.save(attachments);
  },
  delete: async (id: string) => {
    const attachments = await attachmentService.getAll();
    await attachmentService.save(attachments.filter(a => a.id !== id));
  },
  // 上传文件（返回base64）
  upload: async (file: File, type: 'image' | 'document' | 'other' = 'other'): Promise<Attachment> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const attachment: Attachment = {
          id: Date.now().toString(),
          name: file.name,
          url: reader.result as string,
          type,
          size: file.size,
          uploadTime: new Date().toISOString(),
        };
        await attachmentService.add(attachment);
        resolve(attachment);
      };
      reader.readAsDataURL(file);
    });
  },
};

// 操作日志CRUD
export const operationLogService = {
  getAll: async (): Promise<OperationLog[]> => {
    const logs = await idbStorage.getList<OperationLog>(STORES.OPERATION_LOGS);
    return logs.sort((a: OperationLog, b: OperationLog) => 
      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  },
  add: async (log: Omit<OperationLog, 'id' | 'timestamp'>) => {
    const logs = await operationLogService.getAll();
    const newLog: OperationLog = {
      ...log,
      id: Date.now().toString(),
      timestamp: new Date().toISOString(),
    };
    logs.unshift(newLog);
    // 只保留最近100条日志
    if (logs.length > 100) {
      logs.pop();
    }
    await idbStorage.saveList(KEYS.OPERATION_LOGS, STORES.OPERATION_LOGS, logs);
  },
  clear: async () => {
    await idbStorage.saveList(KEYS.OPERATION_LOGS, STORES.OPERATION_LOGS, []);
  },
};

// 简历投递CRUD
export const jobApplicationService = {
  getAll: async (): Promise<JobApplication[]> => {
    const apps = await idbStorage.getList<JobApplication>(STORES.JOB_APPLICATIONS);
    return apps.sort((a: JobApplication, b: JobApplication) => 
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  },
  getByJobId: async (jobId: string): Promise<JobApplication[]> => {
    const apps = await jobApplicationService.getAll();
    return apps.filter(a => a.jobId === jobId);
  },
  getById: async (id: string): Promise<JobApplication | undefined> => {
    const apps = await jobApplicationService.getAll();
    return apps.find(a => a.id === id);
  },
  save: async (apps: JobApplication[]) => {
    await idbStorage.saveList(KEYS.JOB_APPLICATIONS, STORES.JOB_APPLICATIONS, apps);
  },
  add: async (application: JobApplication) => {
    const apps = await jobApplicationService.getAll();
    apps.push(application);
    await jobApplicationService.save(apps);
  },
  update: async (id: string, data: Partial<JobApplication>) => {
    const apps = await jobApplicationService.getAll();
    const index = apps.findIndex(a => a.id === id);
    if (index !== -1) {
      apps[index] = { ...apps[index], ...data };
      await jobApplicationService.save(apps);
    }
  },
  delete: async (id: string) => {
    const apps = await jobApplicationService.getAll();
    await jobApplicationService.save(apps.filter(a => a.id !== id));
  },
};

// 邮件服务（模拟）
export const emailService = {
  sendInquiryNotification: async (inquiry: Inquiry): Promise<boolean> => {
    const config = await systemConfigService.get();
    const notifyEmail = config.notifyEmail || 'sales@hypwr.com';
    
    await operationLogService.add({
      user: 'system',
      action: '发送询单通知邮件',
      module: '邮件',
      details: `询单来自: ${inquiry.name}, 产品: ${inquiry.productModel}, 发送到: ${notifyEmail}`,
    });
    
    console.log(`[邮件通知] 询单通知已发送到 ${notifyEmail}`);
    return true;
  },
  sendJobApplicationNotification: async (application: JobApplication): Promise<boolean> => {
    const config = await systemConfigService.get();
    const notifyEmail = config.notifyEmail || 'hr@hypwr.com';
    
    await operationLogService.add({
      user: 'system',
      action: '发送简历投递通知邮件',
      module: '邮件',
      details: `简历来自: ${application.name}, 职位: ${application.jobTitle}, 发送到: ${notifyEmail}`,
    });
    
    console.log(`[邮件通知] 简历投递通知已发送到 ${notifyEmail}`);
    return true;
  },
  sendEmail: async (email: { to: string; subject: string; body: string; from: string }): Promise<boolean> => {
    await operationLogService.add({
      user: 'system',
      action: '发送邮件',
      module: '邮件',
      details: `邮件主题: ${email.subject}, 收件人: ${email.to}`,
    });
    
    console.log(`[邮件发送] 收件人: ${email.to}`);
    return true;
  },
};
